import React, {useCallback, useLayoutEffect, useRef} from "react";
import {getElements, PrintPurchaseViewTableTypes} from "../index";
import {createUUId} from "../../../../../core/services/utils/utils";
import Sizes from "../../../../../core/models/static/sizes";

/**
 * @param {PrintPurchasePage} page
 * @return {JSX.Element}
 */
const PrintPurchaseViewPage = ({page}) => {
    /** @type {React.MutableRefObject<HTMLTableElement | null>} */
    const pageRef = useRef(null);
    const productsFillerId = useRef(createUUId(true));

    /**
     * Clears the table entries except for the colgroup.
     */
    const clearTable = useCallback(() => {
        for (const index of pageRef.current.childNodes.keys()) {
            if (index !== 0) {
                pageRef.current.removeChild(pageRef.current.childNodes.item(index));
            }
        }
    }, []);

    /**
     * Creates the body entries of the page based on given range and type.
     * @param {string} type
     * @param {[number, number]} range
     * @param {any} elements
     * @return {Node|null}
     */
    const createTableBody = useCallback((type, range, elements) => {
        if (!range.length) {
            return null;
        }
        let element;
        switch (type) {
            case PrintPurchaseViewTableTypes.products:
                element = elements.productsTable.body.cloneNode(true);
                break;
            default:
                break;
        }
        if (!element) {
            return null;
        }
        const children = [...element.childNodes.values()];
        for (let i = 0; i < children.length; i++) {
            if (i > range[1] || i < range[0]) {
                element.removeChild(children[i]);
            }
        }

        const spacerElements = [
            elements.productsTable.bodyFiller.id,
        ];
        for (const elementElement of element.children) {
            if (spacerElements.includes(elementElement.id)) {
                elementElement.setAttribute('id', productsFillerId.current);
            }
        }
        return element;
    }, []);

    /**
     * Fetches the page elements from the template to be shown in the page.
     * @param {PrintPurchasePage} pageData
     * @param {any} elements
     */
    const fetchTemplateElements = useCallback((pageData, elements) => {
        const result = [
            elements.pageHeader.cloneNode(true),
        ];
        for (const entity of pageData.entities) {
            switch (entity.name) {
                case PrintPurchaseViewTableTypes.products: {
                    const body = createTableBody(entity.name, entity.range, elements);
                    result.push(elements.productsTable.header.cloneNode(true));
                    if (body) {
                        result.push(body);
                    }
                    if (entity.useContinueFooter) {
                        result.push(elements.productsTable.footerContinue.cloneNode(true));
                    } else {
                        result.push(elements.productsTable.footer.cloneNode(true));
                    }
                    break;
                }
                default:
                    break;
            }
        }
        result.forEach((e) => {
            e?.removeAttribute('id');
        });
        return result;
    }, [createTableBody]);

    /**
     * Injects the given elements into the page content and sets the page number.
     * @param {Node[]} elements
     */
    const setPageElements = useCallback((elements) => {
        pageRef.current.append(...elements);
        const pageIdentifier = pageRef.current.getElementsByClassName('page-identifier')?.item(0);
        if (!pageIdentifier) {
            return;
        }
        pageIdentifier.innerText = `-- ${page.pageNumber} of ${page.totalPages} --`;
    }, [page]);

    /**
     * Fills the remaining spot of the products table in the current page.
     * @param {PrintPurchasePage} pageData
     * @param {any} elements
     */
    const fillRemainingSpots = useCallback((pageData, elements) => {
        const remainingHeight = Math.max(
            elements.page.clientHeight
            - (pageRef.current?.clientHeight ?? 0)
            - Sizes.defaultPagePadding * 2,
            0,
        );
        if (remainingHeight === 0) {
            return;
        }
        if (!pageData.entities.find((e) => e.name === PrintPurchaseViewTableTypes.products)) {
            return;
        }
        // Filler id is on the <tr>; only stretch that row’s cells (not sibling product rows).
        const fillerRow = document.getElementById(productsFillerId.current);
        if (!fillerRow) {
            return;
        }
        const height = `${remainingHeight}px`;
        fillerRow.setAttribute('height', height);
        for (const cell of fillerRow.children) {
            cell.setAttribute('height', height);
        }
    }, []);

    /**
     * With any changes in page:
     * - clears the table entries
     * - populates the page with the proper entries based on the values of the page.
     */
    useLayoutEffect(() => {
        if (!pageRef.current) {
            return;
        }
        clearTable();
        const elements = getElements();
        setPageElements(fetchTemplateElements(page, elements));
        requestAnimationFrame(() => {
            fillRemainingSpots(page, elements);
        });
    }, [clearTable, fetchTemplateElements, fillRemainingSpots, page, setPageElements]);

    return (
        <div className={'print-invoice-page'}>
            <table ref={pageRef} width={'100%'} className={'table'}>
                <colgroup>
                    {
                        Array(60).fill(1).map((_, index) => (
                            <col key={index} width="1.6666666666667%"/>
                        ))
                    }
                </colgroup>
            </table>
        </div>
    );
};

export default PrintPurchaseViewPage;
